import { ModalName } from '@share/enums';
import {
  handleInputMaxLength,
  handleInputNumberOnly,
  showPercentage,
  toggleBootstrapModal,
} from '@util/helper';
import { listing } from '@state/private';
import { market } from '@state/market';
import { toast } from 'react-toastify';
import { useCurrency } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DotLoading from '@com/utils/DotLoading';
import ErrorMsg from '@com/utils/ErrorMsg';

interface Params {
  tokenId: number;
  collectionAddress: string;
  royalty: number;
  loadLaunchpadData: () => void;
}

interface FormInputs {
  orderCurrencyId: number;
  floorPrice: number;
  orderDate: string;
  orderTime: string;
  orderIncrease: number;
}

function CreateAuctionModal(param: Params): JSX.Element {
  /* Define state / hook */
  const { createAuction, isSigned, payment } = market.useContainer();
  const { getMinimumUnit } = useCurrency();
  const { t } = useTranslation();
  const { allowedCurrencies, getServiceRate, getHandlingRate, getMethodId } =
    listing.useContainer();
  const { loadLaunchpadData } = param;

  /* Custom Data Object for render or other usages */
  const [isLoading, setIsLoading] = useState(false);
  const [quantity] = useState(1);
  const {
    register,
    watch,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInputs>();

  const selectedCurrencyId = Number(watch('orderCurrencyId'));

  /* Render control function / logic */
  const handleSubmitAuction = useDebouncedCallback(
    async (formData: FormInputs) => {
      // Format: 2020-05-14T04:00:00Z
      const endtime = Math.round(
        new Date(`${formData.orderDate} ${formData.orderTime}:00`).getTime() /
          1000
      );
      const now = Math.round(Date.now() / 1000);
      const diff = endtime - now;
      if (diff < 60 * 60 * 1 || diff > 60 * 60 * 24 * 7) {
        toast.error(t('toastMessage.futureTimeRequired'));
        return;
      }

      const isLogin = await payment();
      if (!isLogin) return;
      setIsLoading(true);

      const isTxSuccess = await createAuction(
        param.collectionAddress,
        param.tokenId,
        selectedCurrencyId,
        getMethodId(selectedCurrencyId),
        quantity,
        formData.floorPrice,
        formData.orderIncrease,
        endtime
      );
      setIsLoading(false);
      if (!isTxSuccess) return;
      toggleBootstrapModal(ModalName.CREATE_BID);
      loadLaunchpadData();
    },
    500,
    { leading: true, trailing: false }
  );

  /* Data Change Listeners - useEffect/useMemo/useCallback */
  useEffect(() => {
    // Default value not working, temp solution
    const id = allowedCurrencies?.[0]?.id;
    if (id) setValue('orderCurrencyId', id);
  }, [allowedCurrencies]);

  /* Styling */

  /* Render */
  return (
    <div
      aria-hidden
      className="modal fade popup"
      id={ModalName.CREATE_BID}
      role="dialog"
    >
      <div
        className="modal-dialog modal-dialog-centered mt-0 mb-0"
        role="document"
      >
        <div className="modal-content">
          <button
            className="close"
            type="button"
            onClick={() => {
              if (!isLoading) toggleBootstrapModal(ModalName.CREATE_BID);
            }}
          >
            <span aria-hidden>&times;</span>
          </button>

          <div className="modal-body space-y-20 pd-40">
            <h3>{t('modal.listItemForAuction')}</h3>
            <hr />
            {isLoading ? (
              <div>
                <DotLoading />
                <div className="row mt-4">
                  <div
                    className="col-12 text-center mt-5"
                    style={{ fontSize: '2.5em' }}
                  >
                    {isSigned
                      ? t('modal.waitContract')
                      : t('modal.signContract')}
                  </div>
                </div>
              </div>
            ) : (
              <form noValidate onSubmit={handleSubmit(handleSubmitAuction)}>
                <p>{t('modal.flooringPrice')}</p>
                <input
                  aria-required
                  aria-label={t('modal.egSellingPrice')}
                  className="form-control mt-3"
                  data-test="floor-price-input"
                  inputMode="decimal"
                  maxLength={12}
                  min={0}
                  placeholder={t('modal.egSellingPrice')}
                  step={getMinimumUnit(selectedCurrencyId)}
                  type="number"
                  onKeyDown={(e) =>
                    handleInputMaxLength(e) && handleInputNumberOnly(e)
                  }
                  {...register('floorPrice', {
                    valueAsNumber: true,
                    required: {
                      value: true,
                      message: t('modal.priceRequired'),
                    },
                    validate: {
                      value: (value) => {
                        const minimumUnit = getMinimumUnit(selectedCurrencyId);
                        const digits = String(value).split('.')?.[1]?.length;
                        const maxDigits =
                          String(minimumUnit).split('.')[1]?.length;

                        return (
                          digits === undefined ||
                          digits <= maxDigits ||
                          t('modal.pricePatternRequired', {
                            min: minimumUnit,
                          })
                        );
                      },
                    },
                  })}
                />
                <ErrorMsg errors={errors} name="floorPrice" />

                <div className="d-flex justify-content-between mb-5 mt-5">
                  <p>{t('modal.chooseReceiveCurrency')}</p>
                  <div className="col-5 pr-0">
                    <select
                      className="p-2 w-100 text-center bg-transparent rounded-pill"
                      data-test="auction-currency-select"
                      {...register('orderCurrencyId', {
                        valueAsNumber: true,
                        required: {
                          value: true,
                          message: t('modal.chooseCurrecny'),
                        },
                      })}
                    >
                      {allowedCurrencies?.map((item) => (
                        <option key={item.id} value={item.id.toString()}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    <ErrorMsg errors={errors} name="orderCurrencyId" />
                  </div>
                </div>

                <div className="d-flex justify-content-between">
                  <p>{t('modal.auctionExpirationDate')}</p>
                </div>

                <div className="d-flex justify-content-between mt-3">
                  <input
                    aria-required
                    className="list-details"
                    data-test="date-input"
                    type="date"
                    {...register('orderDate', {
                      required: true,
                    })}
                  />
                  <input
                    aria-required
                    className="list-details"
                    data-test="time-input"
                    type="time"
                    {...register('orderTime', {
                      required: true,
                    })}
                  />
                </div>
                <div className="d-flex justify-content-between mb-5">
                  {String(errors?.orderDate?.type) === 'required' && (
                    <p className="ErrorMsg">{t('modal.vaildDateRequired')}</p>
                  )}
                  {String(errors?.orderTime?.type) === 'required' && (
                    <p className="ErrorMsg">{t('modal.vaildTimeRequired')}</p>
                  )}
                </div>

                <div className="d-flex justify-content-between mb-5">
                  <p className="mt-1">{t('modal.bidIncreasePercentage')}</p>
                  <p>
                    <select
                      className="p-1 w-100 text-center bg-transparent rounded"
                      data-test="percentage-select"
                      {...register('orderIncrease', {
                        valueAsNumber: true,
                        required: true,
                      })}
                    >
                      {[1, 3, 5, 10, 15, 20].map((p) => (
                        <option key={p} value={p}>
                          {showPercentage(p)}
                        </option>
                      ))}
                    </select>
                  </p>
                </div>
                <div className="d-flex justify-content-between mb-5">
                  <p>{t('itemDetails.artistRoyalties')}</p>
                  <div className="text-right">
                    <div className="price color-popup">
                      {showPercentage(param.royalty)}
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-between mb-5">
                  <p>{t('modal.selectServiceFee')}</p>
                  <div className="text-right">
                    <div className="price color-popup">
                      {showPercentage(getServiceRate(selectedCurrencyId))}
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-between mb-5">
                  <p>{t('modal.selectHandlingFee')}</p>
                  <div className="text-right">
                    <div className="price color-popup">
                      {showPercentage(getHandlingRate(selectedCurrencyId))}
                    </div>
                  </div>
                </div>
                <button
                  aria-label={t('modal.order')}
                  className="btn btn-primary"
                  data-test="order-btn"
                  type="submit"
                >
                  <span>{t('modal.order')}</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateAuctionModal;
